import { Container } from '@mui/material';
import { Outlet, type RouteObject } from 'react-router';
import { RedirectIfAuthenticated, RequireAuth } from '@/auth';
import { RouteError } from '@/components/RouteError';
import { AuthLayout } from '@/layouts/AuthLayout';
import { AccountPage } from '@/pages/AccountPage';
import { AuthCompletePage } from '@/pages/auth/AuthCompletePage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { SessionGate } from '@/session/SessionGate';
import { legalRoutes } from '@/legal/routes';
import { MarketplaceLayout } from './MarketplaceLayout';
import { MarketplaceHomePage } from './pages/MarketplaceHomePage';

const lazyPage = (loader: () => Promise<{ Component: React.ComponentType }>) => ({ lazy: loader });

/**
 * The marketplace host serves its own route tree: public pages first (search
 * engines and visitors), then what needs a session.
 */
export function buildMarketplaceRoutes(): RouteObject[] {
  return [
    {
      errorElement: <RouteError />,
      children: [
        {
          element: (
            <RedirectIfAuthenticated>
              <AuthLayout />
            </RedirectIfAuthenticated>
          ),
          children: [{ path: '/login', element: <LoginPage /> }],
        },
        { path: '/auth/complete', element: <AuthCompletePage /> },
        {
          element: <MarketplaceLayout />,
          children: [
            { index: true, element: <MarketplaceHomePage /> },
            ...legalRoutes(),
            {
              path: '/buscar',
              ...lazyPage(async () => ({
                Component: (await import('./pages/SearchPage')).SearchPage,
              })),
            },
            {
              path: '/personal-em/:uf/:city',
              ...lazyPage(async () => ({
                Component: (await import('./pages/SearchPage')).SearchPage,
              })),
            },
            {
              path: '/personal-para/:specialty',
              ...lazyPage(async () => ({
                Component: (await import('./pages/SearchPage')).SearchPage,
              })),
            },
            {
              path: '/personal-online',
              ...lazyPage(async () => ({
                Component: (await import('./pages/SearchPage')).SearchPage,
              })),
            },
            {
              path: '/guias',
              ...lazyPage(async () => ({
                Component: (await import('@/guides/GuidesIndexPage')).GuidesIndexPage,
              })),
            },
            {
              path: '/guias/:slug',
              ...lazyPage(async () => ({
                Component: (await import('@/guides/GuidePage')).GuidePage,
              })),
            },
            {
              path: '/personal/:slug',
              ...lazyPage(async () => ({
                Component: (await import('./pages/ProfilePage')).ProfilePage,
              })),
            },
            {
              path: '/para-personais',
              ...lazyPage(async () => ({
                Component: (await import('./pages/ForTrainersPage')).ForTrainersPage,
              })),
            },
            {
              path: '/como-funciona',
              ...lazyPage(async () => ({
                Component: (await import('./pages/HowItWorksPage')).HowItWorksPage,
              })),
            },
            {
              element: (
                <RequireAuth>
                  <SessionGate>
                    <Outlet />
                  </SessionGate>
                </RequireAuth>
              ),
              children: [
                {
                  path: '/meus-pedidos',
                  ...lazyPage(async () => ({
                    Component: (await import('./pages/MyRequestsPage')).MyRequestsPage,
                  })),
                },
                {
                  path: '/painel',
                  ...lazyPage(async () => ({
                    Component: (await import('./pages/DashboardPage')).DashboardPage,
                  })),
                },
                {
                  path: '/moderacao',
                  ...lazyPage(async () => ({
                    Component: (await import('./pages/ModerationPage')).ModerationPage,
                  })),
                },
                {
                  path: '/conta',
                  element: (
                    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
                      <AccountPage />
                    </Container>
                  ),
                },
              ],
            },
            { path: '*', element: <NotFoundPage /> },
          ],
        },
      ],
    },
  ];
}
